import {
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonCard,
  IonButtons,
  IonFooter,
  IonToolbar,
  useIonToast,
  IonLoading,
  IonToggle,
} from "@ionic/react";

import {useMemo, useState} from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Category } from "../../../@types/Category";
import InputErrorMessage from "../../../components/InputErrorMessage";
import { findFileTypes } from "../../../store/fileTypes/actions/findFileTypes";
import { updateFileType } from "../../../store/fileTypes/actions/updateFileType";
import { selectFileTypes } from "../../../store/fileTypes/selectors/selectFileTypes";
import { selectUpdateFileTypeStatus } from "../../../store/fileTypes/selectors/selectUpdateFileTypeStatus";

export const FileTypeDetailForm = ({ idFileType }: { idFileType: string }) => {
  const dispatch = useDispatch();
  const updateFileTypeStatus = useSelector(selectUpdateFileTypeStatus);
  const fileTypes = useSelector(selectFileTypes);
  const fileTypeDetail = fileTypes.find((element) => element?.id == idFileType);
  const [presentToast] = useIonToast();

  const [fileType, setFileType] = useState<Category>(fileTypeDetail!);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const handleLoadingFinish = () => {
    if (updateFileTypeStatus === "succeeded") {
      presentToast({
        message: "Tipo de archivo actualizado correctamente",
        duration: 3000,
        color: "success",
      });
      dispatch(findFileTypes());
    }
    if (updateFileTypeStatus === "failed") {
      presentToast({
        message: "Ocurrió un error al tratar de actualizar el tipo de archivo",
        duration: 3000,
        color: "danger",
      });
    }
  };

  const handleOnChange = (type: string, checked: boolean) => {
    switch (type) {
      case "isSignable":
        setFileType({ ...fileType, isSignable: checked });
        break;
      case "datesRequire":
        setFileType({ ...fileType, datesRequire: checked });
        break;
      case "enabled":
        setFileType({ ...fileType, enabled: checked });
        break;
      case "requireCode":
        setFileType({ ...fileType, requireCode: checked });
        break;
      case "sendMessageWhatsApp":
        setFileType({ ...fileType, sendMessageWhatsApp: checked });
        break;
      case "sendMessageSMS":
        setFileType({ ...fileType, sendMessageSMS: checked });
        break;
      default:
        break;
    }
    setButtonDisabled(false);
  };

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      name: fileType.name,
      expirationDays: fileType.expirationDays,
      htmlDescription: fileType.htmlDescription,
    },
  });

  const onSubmit = async (data: any) => {
    const finalFileType = {
      ...fileType,
      id: fileType.id,
      name: data.name,
      expirationDays: data.expirationDays,
      htmlDescription: data.htmlDescription,
    } as Category;
    dispatch(updateFileType(finalFileType));
  };

  const controllers = useMemo(()=>{
    return [({ field: { onChange, value } }:any) => (
      <IonInput
        value={value}
        placeholder="Ingrese el nombre del tipo de archivo"
        onIonChange={({ detail: { value } }) => {
          onChange(value);
          setButtonDisabled(false);
        }}
        {...register("name", {
          required: "Campo obligatorio.",
        })}
      />
    ),({ field: { onChange, value } }:any) => (
      <IonInput
        value={value}
        type="number"
        placeholder="Ingrese cantidad de días"
        onIonChange={({ detail: { value } }) => {
          onChange(value);
          setButtonDisabled(false);
        }}
        {...register("expirationDays", {
          valueAsNumber: true,
        })}
      />
    ),({ field: { onChange, value } }:any) => (
      <IonInput
        value={value}
        placeholder="Ingrese descripción"
        onIonChange={({ detail: { value } }) => {
          onChange(value);
          setButtonDisabled(false);
        }}
        {...register("htmlDescription", {
          // required: "Campo obligatorio.",
        })}
      />
    )]
  },[])

  return (
    <>
      <form noValidate onSubmit={handleSubmit(onSubmit)} style={{ padding: 18 }}>
        <IonCard style={{ maxWidth: 700 }}>
          <section className="ion-padding">
            <IonItem lines="inset">
              <IonLabel position="stacked">Nombre</IonLabel>
              <Controller
                name="name"
                control={control}
                render={controllers[0]}
              />
            </IonItem>
            <InputErrorMessage errorMessage={errors.name?.message || ""} paddingLeft={15} />

            <IonItem>
              <IonLabel>Firmable</IonLabel>
              <IonToggle
                checked={fileType.isSignable}
                onIonChange={({ detail: { checked } }) => handleOnChange("isSignable", checked)}
              />
            </IonItem>

            <IonItem>
              <IonLabel>Requiere fecha de vencimiento</IonLabel>
              <IonToggle
                checked={fileType.datesRequire}
                onIonChange={({ detail: { checked } }) => handleOnChange("datesRequire", checked)}
              />
            </IonItem>

            <IonItem lines="inset">
              <IonLabel position="stacked">Aviso de días para vencer</IonLabel>
              <Controller
                name="expirationDays"
                control={control}
                render={controllers[1]}
              />
            </IonItem>
            <InputErrorMessage errorMessage={errors.expirationDays?.message || ""} />

            <IonItem lines="inset">
              <IonLabel position="stacked">Descripción</IonLabel>
              <Controller
                name="htmlDescription"
                control={control}
                render={controllers[2]}
              />
            </IonItem>
            <InputErrorMessage
              errorMessage={errors.htmlDescription?.message || ""}
              paddingLeft={15}
            />

            <IonItem>
              <IonLabel>Activo</IonLabel>
              <IonToggle
                checked={fileType.enabled}
                onIonChange={({ detail: { checked } }) => handleOnChange("enabled", checked)}
              />
            </IonItem>

            <IonItem>
              <IonLabel>Requiere ficha</IonLabel>
              <IonToggle
                checked={fileType.requireCode}
                onIonChange={({ detail: { checked } }) => handleOnChange("requireCode", checked)}
              />
            </IonItem>

            <IonItem>
              <IonLabel>Notificación automática vía WhatsApp</IonLabel>
              <IonToggle
                checked={fileType.sendMessageWhatsApp}
                onIonChange={({ detail: { checked } }) =>
                  handleOnChange("sendMessageWhatsApp", checked)
                }
              />
            </IonItem>

            <IonItem>
              <IonLabel>Notificación automática vía SMS</IonLabel>
              <IonToggle
                checked={fileType.sendMessageSMS}
                onIonChange={({ detail: { checked } }) => handleOnChange("sendMessageSMS", checked)}
              />
            </IonItem>

            <br />
          </section>

          <IonFooter>
            <IonToolbar color="light">
              <IonButtons slot="end">
                <IonButton disabled={buttonDisabled} fill="solid" type="submit">
                  Actualizar
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonFooter>
        </IonCard>

        <IonLoading
          isOpen={updateFileTypeStatus === "pending"}
          message={"Actualizando tipo de archivo..."}
          onDidDismiss={handleLoadingFinish}
        />
      </form>
    </>
  );
};
